import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './AirtimeDetail.css';

// Simple Modal Component for Confirmation (Re-used)
const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h4>Confirm Purchase</h4>
        <p>{message}</p>
        <div className="modal-actions">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={onConfirm} className="btn-primary">Confirm</button>
        </div>
      </div>
    </div>
  );
};

function AirtimeDetail() {
  const { provider } = useParams();
  const navigate = useNavigate();
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [bonusRate, setBonusRate] = useState(0);
  const [expectedAmount, setExpectedAmount] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false); // State for confirmation modal
  
  // Set provider details based on URL parameter
  useEffect(() => {
    if (!['safaricom', 'airtel', 'telkom'].includes(provider)) {
      // Redirect to airtime page if invalid provider
      navigate('/buy-airtime');
      return;
    }
    
    // Set bonus rate based on provider
    switch(provider) {
      case 'safaricom':
        setBonusRate(0.1);
        break;
      case 'airtel':
	setBonusRate(0.1)
	break;
      case 'telkom':
	setBonusRate(0.1)
	break;
      default:
        setBonusRate(0);
        break;
    }
  }, [provider, navigate]);

  // Calculate expected amount when amount or bonus rate changes
  useEffect(() => {
    if (amount && !isNaN(amount)) {
      setExpectedAmount((parseFloat(amount) * (1 + bonusRate)).toFixed(2));
    } else {
      setExpectedAmount(0);
    }
  }, [amount, bonusRate]);

  const validateForm = () => {
    const newErrors = {};
    
    // Validate phone number (simple validation for demo)
    if (!phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10,12}$/.test(phoneNumber)) { // Basic Kenyan phone number format check
      newErrors.phoneNumber = 'Please enter a valid 10-12 digit phone number';
    }
    
    // Validate amount
    if (!amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(amount) || parseFloat(amount) < 10) {
      newErrors.amount = 'Amount must be at least 10';
    } else if (parseFloat(amount) > 10000) {
      newErrors.amount = 'Amount cannot exceed 10,000';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsConfirming(true); // Open confirmation modal
    }
  };

  const handleConfirm = () => {
    setIsConfirming(false); // Close modal
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      // Reset form and navigate after delay
      setTimeout(() => {
        setPhoneNumber('');
        setAmount('');
        setSubmitSuccess(false);
        // Navigate back to airtime selection page after successful submission
        navigate('/buy-airtime'); 
      }, 20000);
    }, 1000);
  };

  const handleCancel = () => {
    setIsConfirming(false); // Close modal
  };

  const getProviderTitle = () => {
    switch(provider) {
      case 'safaricom':
        return 'Buy Safaricom Airtime (10% Bonus)';
      case 'airtel':
        return 'Buy Airtel Airtime (10% Bonus)';
      case 'telkom':
        return 'Buy Telkom Airtime (10% Bonus)';
      default:
        return 'Buy Airtime';
    }
  };

  const getConfirmationMessage = () => {
    let message = `Are you sure you want to buy Ksh.${amount} ${provider} airtime for phone number ${phoneNumber}?`;
    if (bonusRate > 0) {
      message += ` You will receive airtime worth Ksh.${expectedAmount}.`;
    }
    return message;
  };

  return (
    <div className="airtime-detail-page">
      <div className="container">
        <div className="back-button" onClick={() => navigate('/buy-airtime')}>
          &larr; Back to Airtime Options
        </div>
        
        <h1>{getProviderTitle()}</h1>
        
        <div className="provider-info">
          <div className={`provider-logo ${provider}-logo`}></div>
          {provider === 'safaricom' && <p>Get 12% extra airtime when you buy Safaricom airtime</p>}
          {provider === 'airtel' && <p>Get 10% extra airtime when you buy Airtel airtime</p>}
          {provider === 'telkom' && <p>Get 10% extra airtime when you buy Telkom airtime</p>}
        </div>
        
        <div className="airtime-form-container">
          {submitSuccess ? (
            <div className="success-message">
              <h3>Please follow these steps:</h3>
              <p>To complete your purchase of Ksh.{amount} {provider} airtime, pay Ksh.{amount} to the Paybill 4187671, Account number: Your phone number</p>
              {(
                <p>You will receive airtime worth Ksh.{expectedAmount}.</p>
              )}
            </div>
          ) : (
            <form className="airtime-form" onSubmit={handleFormSubmit}> {/* Changed onSubmit handler */}
              <div className="form-group">
                <label>Enter phone number</label>
                <input 
                  type="tel" // Use type="tel" for phone numbers
                  placeholder="e.g., 0712345678" 
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={errors.phoneNumber ? 'error' : ''}
                  maxLength="12" // Add maxLength
                />
                {errors.phoneNumber && <div className="error-message">{errors.phoneNumber}</div>}
              </div>
              
              <div className="form-group">
                <label>Amount to purchase</label>
                <input 
                  type="number" 
                  placeholder="Min=10, Max=10000" 
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={errors.amount ? 'error' : ''}
                  min="10" // Add min/max attributes
                  max="10000"
                />
                {errors.amount && <div className="error-message">{errors.amount}</div>}
              </div>
              
              {(
                <div className="form-info">
                  <p>You will receive airtime worth Ksh.{expectedAmount}</p>
                </div>
              )}
              
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting} // Keep disabled during actual submission
              >
                {isSubmitting ? 'Processing...' : 'Buy Airtime'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal 
        isOpen={isConfirming}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        message={getConfirmationMessage()}
      />
    </div>
  );
}

export default AirtimeDetail;
